// SkillTable — list view used on /skills (company + global scopes).
//
// `mode` controls per-row chrome:
//   "company" (default) — full row: version chip, state badge, Global pill,
//     trailing Edit chip; whole row is a <Link> to /skills/:id/edit.
//   "global" — starter-skill catalog: hide version chip / state badge /
//     Global pill, trailing button is either "Import to my Company"
//     (clones into the current company) or, if already cloned, a chip-
//     link to open the local copy. Row wrapper becomes a <div> so the
//     button doesn't fight a parent navigation.
import { Link, router } from "@inertiajs/react"
import { Link2 } from "lucide-react"
import { Badge, type BadgeTone } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { RowAvatar } from "./row-avatar"
import { fmtNumber, fmtRelativeTime } from "@/lib/format"
import type { Skill, SkillCategory, SkillStatus } from "@/types/skill"

type Props = {
  skills: Skill[]
  mode?: "company" | "global"
}

const CATEGORY_LABEL: Record<SkillCategory, string> = {
  knowledge_source: "Knowledge Source",
  response_action:  "Response Action",
  routing_logic:    "Routing Logic",
  workflow:         "Workflow",
  integration:      "Integration",
}

const STATUS_TONE: Record<SkillStatus, BadgeTone> = {
  live:     "gold",
  draft:    "neutral",
  archived: "red",
}

const STATUS_LABEL: Record<SkillStatus, string> = {
  live:     "Live",
  draft:    "Draft",
  archived: "Archived",
}

export function SkillTable({ skills, mode = "company" }: Props) {
  if (skills.length === 0) {
    return (
      <div className="rounded-card border border-border bg-app-panel p-6 text-sm text-app-muted">
        No skills in this view.
      </div>
    )
  }

  const isGlobalMode = mode === "global"

  return (
    <div className="overflow-hidden rounded-card border border-border bg-app-panel">
      <div className="divide-y divide-border">
        {skills.map((s) => {
          const rowClass =
            "grid grid-cols-[auto_minmax(0,1fr)_160px_110px_90px_70px_auto] items-center gap-4 px-5 py-4 text-sm transition outline-none hover:bg-white/5 focus-visible:bg-white/5 focus-visible:ring-1 focus-visible:ring-brand-red/40"

          const innerCells = (
            <>
              <RowAvatar name={s.name} />

              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate font-semibold text-white">{s.name}</span>
                  {!isGlobalMode && (
                    <>
                      <span
                        className={
                          s.live
                            ? "rounded-chip border border-vip-gold/30 bg-vip-goldMute px-1.5 py-0.5 text-[10px] font-medium text-vip-goldSoft"
                            : "rounded-chip border border-brand-red/30 bg-brand-redSoft px-1.5 py-0.5 text-[10px] font-medium text-brand-red"
                        }
                      >
                        {s.versionLabel}
                      </span>
                      {s.isGlobal && (
                        <span className="rounded-chip border border-border bg-white/5 px-1.5 py-0.5 text-[10px] font-medium text-app-muted">
                          Global
                        </span>
                      )}
                    </>
                  )}
                </div>
                <div className="truncate text-xs text-app-muted">
                  Used {fmtNumber(s.usageCount)} times · updated {fmtRelativeTime(s.updatedAt)}
                </div>
                {isGlobalMode && s.dependencyKeys && s.dependencyKeys.length > 0 && (
                  <div className="mt-1 flex items-center gap-1 truncate text-[11px] text-app-muted">
                    <Link2 size={11} className="shrink-0" />
                    <span className="truncate">Also imports: {s.dependencyKeys.join(", ")}</span>
                  </div>
                )}
              </div>

              <div className="text-app-muted">{CATEGORY_LABEL[s.category]}</div>

              <div>
                {!isGlobalMode && (
                  <Badge tone={STATUS_TONE[s.status]}>{STATUS_LABEL[s.status]}</Badge>
                )}
              </div>

              <div className="text-app-muted">
                {s.agentIds.length} agent{s.agentIds.length === 1 ? "" : "s"}
              </div>

              <div className="text-right font-semibold text-vip-goldSoft">
                {s.successRate == null ? "—" : `${Math.round(s.successRate * 100)}%`}
              </div>

              {isGlobalMode ? (
                s.clonedToCurrentCompanySkillId == null ? (
                  <Button
                    type="button"
                    variant="red"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      router.post(`/skills/${s.id}/clone`, {}, {})
                    }}
                  >
                    Import to my Company
                  </Button>
                ) : (
                  <Link
                    href={`/skills/${s.clonedToCurrentCompanySkillId}/test`}
                    className="text-xs text-status-success hover:underline"
                  >
                    ✓ Already imported · Open
                  </Link>
                )
              ) : (
                <span className="flex items-center gap-1.5">
                  <span className="rounded-control border border-border px-3 py-1.5 text-xs font-medium text-app-text">
                    Designer
                  </span>
                  {/* Direct shortcut to the manual config form. The whole
                      row is a <Link> (anchor), so this MUST be a button —
                      nesting <a> inside <a> trips React's hydration check
                      ("<a> cannot be a descendant of <a>"). We
                      stopPropagation + router.visit to navigate. */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      router.visit(`/skills/${s.id}/edit`)
                    }}
                    className="rounded-control border border-border px-3 py-1.5 text-xs font-medium text-app-muted transition hover:bg-white/5 hover:text-app-text"
                  >
                    Manual edit
                  </button>
                </span>
              )}
            </>
          )

          if (isGlobalMode) {
            return (
              <div key={s.id} className={rowClass}>
                {innerCells}
              </div>
            )
          }

          return (
            <Link key={s.id} href={`/skills/${s.id}/test`} className={rowClass}>
              {innerCells}
            </Link>
          )
        })}
      </div>
    </div>
  )
}
